/**
 * Brand Mentions Tab — "who cited you this week across LLMs" feed.
 * Backed by DataForSEO LLM Mentions (Google AI Overviews + ChatGPT).
 * Opt-in per client via `brand_mentions_enabled`.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import {
  MessageSquareQuote, Loader2, RefreshCw, ExternalLink, Info, Globe, Sparkles, TrendingUp, Download,
} from "lucide-react";
import { rowsToCsv, downloadCsv, slugForFilename, todayStamp } from "../../lib/csv";

const BM_PLATFORM_LABEL = { google: "Google AI", chat_gpt: "ChatGPT" };
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const PLATFORM_META = {
  google:   { label: "Google AI",  color: "#4285f4", icon: Globe },
  chat_gpt: { label: "ChatGPT",    color: "#10a37f", icon: Sparkles },
};

function PlatformBadge({ platform }) {
  const m = PLATFORM_META[platform] || { label: platform, color: "#a0aec0" };
  return (
    <span
      style={{ background: `${m.color}18`, color: m.color, borderColor: `${m.color}55` }}
      className="inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-widest"
    >
      {m.label}
    </span>
  );
}

// Turn "2026-09-03 13:54:29 +00:00" into "3 Sep · 13:54"
function fmtObserved(raw) {
  if (!raw) return "—";
  const iso = String(raw).replace(" ", "T").replace(/\s(\+\d{2}:\d{2})$/, "$1");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return raw;
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

const DAY_OPTIONS = [
  { value: 7,  label: "7 days" },
  { value: 30, label: "30 days" },
  { value: 0,  label: "All time" }, // 0 → send as no `days` param
];

export default function BrandMentionsTab({ client }) {
  const enabled = !!client?.brand_mentions_enabled;
  const [feed, setFeed]           = useState(null);
  const [loading, setLoading]     = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [platformFilter, setPlatformFilter] = useState("all"); // all | google | chat_gpt
  const [days, setDays]           = useState(7);
  const [expandedRow, setExpandedRow] = useState(null);

  const load = useCallback(async ({ force = false } = {}) => {
    if (!enabled) return;
    (force ? setRefreshing : setLoading)(true);
    try {
      const params = new URLSearchParams({ platform: platformFilter, limit: "25" });
      if (days) params.set("days", String(days));
      if (force) params.set("force", "true");
      const { data } = await api.get(`/clients/${client.id}/brand-mentions?${params}`);
      setFeed(data);
      if (force) toast.success(`Refreshed · ${data.rows.length} mention${data.rows.length === 1 ? "" : "s"} · $${data.cost_this_call}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load brand mentions");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [client?.id, enabled, platformFilter, days]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [client?.id, platformFilter, days]);

  const rows = feed?.rows || [];
  const perPlatform = feed?.per_platform || {};
  const agg = feed?.aggregate || { count: 0, by_platform: {}, top_pages: [], avg_ai_search_volume: null };

  const exportCsv = () => {
    const columns = [
      { header: "Platform", accessor: (r) => BM_PLATFORM_LABEL[r.platform] || r.platform },
      { header: "Question", accessor: (r) => r.question },
      { header: "Cited URL", accessor: (r) => r.cited_url },
      { header: "Cited title", accessor: (r) => r.cited_title },
      { header: "Client position", accessor: (r) => r.client_position ?? "" },
      { header: "AI search volume", accessor: (r) => r.ai_search_volume ?? "" },
      { header: "Source count", accessor: (r) => r.sources_total ?? (r.sources || []).length },
      { header: "Last seen", accessor: (r) => r.last_response_at || "" },
    ];
    const suffix = `${platformFilter}-${days ? `${days}d` : "all-time"}`;
    downloadCsv(
      `${slugForFilename(client.name)}-brand-mentions-${suffix}-${todayStamp()}.csv`,
      rowsToCsv(rows, columns),
    );
  };

  const platformCounts = useMemo(() => {
    // Prefer aggregate.by_platform (windowed by `days`), fall back to total_count when window is off.
    const out = { google: 0, chat_gpt: 0 };
    if (days) {
      Object.assign(out, agg.by_platform);
    } else {
      out.google   = perPlatform.google?.total_count   || 0;
      out.chat_gpt = perPlatform.chat_gpt?.total_count || 0;
    }
    return out;
  }, [days, agg.by_platform, perPlatform]);

  return (
    <div className="space-y-5 pt-2" data-testid="brand-mentions-tab">
      {/* Header */}
      <div className="card-soft p-5 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#4fd1c5] to-[#319795] text-white flex items-center justify-center shrink-0">
            <MessageSquareQuote className="h-5 w-5" strokeWidth={2} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Brand Mentions · LLM Citations</div>
            <h3 className="font-display text-2xl mt-0.5 text-[#1a202c]">Who cited <span className="font-mono-tight text-[#2c7a7b]">{client.domain}</span> across LLMs</h3>
            <p className="text-xs text-[#718096] mt-1">
              {enabled ? (
                <>Live feed from DataForSEO's LLM Mentions API. Google AI Overviews (worldwide) + ChatGPT (US/English only). <strong>Monthly snapshots</strong> persist on the 1st @ 09:00 UTC; manual lookups cached for 6 hours — hit refresh for a live re-fetch.</>
              ) : (
                <>Not yet enabled — flip <strong>Brand Mentions</strong> on in the <strong>Edit Client</strong> screen. Roughly $0.25 per client per monthly snapshot + $0.10/platform per manual refresh.</>
              )}
            </p>
          </div>
        </div>
        {enabled && (
          <button
            onClick={() => load({ force: true })}
            disabled={refreshing || loading || client.frozen}
            title={client.frozen ? "Client is frozen — unfreeze to pull new data" : undefined}
            data-testid="brand-mentions-refresh"
            className="btn-primary inline-flex items-center gap-2"
          >
            {refreshing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        )}
      </div>

      {!enabled ? (
        <div className="card-soft p-10 text-center text-sm text-[#a0aec0]" data-testid="brand-mentions-disabled">
          Enable Brand Mentions on the Edit Client screen to see LLM citations for this domain.
        </div>
      ) : loading ? (
        <div className="card-soft p-10 flex items-center justify-center gap-3 text-sm text-[#a0aec0]">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading citations…
        </div>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
            <div className="card-soft p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Mentions {days ? `· last ${days}d` : `· all time`}</div>
              <div className="font-display text-3xl mt-1 text-[#1a202c]" data-testid="brand-mentions-count">{agg.count}</div>
              <div className="text-[11px] text-[#718096] mt-1">across enabled platforms</div>
            </div>
            <div className="card-soft p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold flex items-center gap-1">
                <Globe className="h-3 w-3" /> Google AI Overviews
              </div>
              <div className="font-display text-3xl mt-1 text-[#1a202c]">{platformCounts.google}</div>
              <div className="text-[11px] text-[#718096] mt-1">
                {perPlatform.google?.total_count ? `${perPlatform.google.total_count} lifetime` : "no data yet"}
              </div>
            </div>
            <div className="card-soft p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold flex items-center gap-1">
                <Sparkles className="h-3 w-3" /> ChatGPT
                <span title="DataForSEO's ChatGPT dataset is US/English only" className="cursor-help text-[#a0aec0]">
                  <Info className="h-3 w-3 inline" />
                </span>
              </div>
              <div className="font-display text-3xl mt-1 text-[#1a202c]">{platformCounts.chat_gpt}</div>
              <div className="text-[11px] text-[#718096] mt-1">
                {perPlatform.chat_gpt?.total_count ? `${perPlatform.chat_gpt.total_count} lifetime` : "no data yet"}
              </div>
            </div>
            <div className="card-soft p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold flex items-center gap-1">
                <TrendingUp className="h-3 w-3" /> Avg AI search volume
              </div>
              <div className="font-display text-3xl mt-1 text-[#1a202c]">
                {agg.avg_ai_search_volume != null ? agg.avg_ai_search_volume.toLocaleString() : "—"}
              </div>
              <div className="text-[11px] text-[#718096] mt-1">across returned prompts</div>
            </div>
          </div>

          {/* Discovery velocity — stacked weekly bar chart of NEW citations
              (bucketed by first_response_at). Always shows the last 12 ISO
              weeks; ignores the days filter deliberately so users see the
              full historical trend even when filtering the feed to "7 days". */}
          {feed?.weekly_history?.some((b) => b.total > 0) && (
            <div className="card-soft p-5" data-testid="brand-mentions-history-chart">
              <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <div className="h-7 w-7 rounded-lg bg-[#4fd1c5]/15 text-[#2c7a7b] flex items-center justify-center">
                    <TrendingUp className="h-3.5 w-3.5" strokeWidth={2.5} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-bold">Citations discovered · last 12 weeks</div>
                    <div className="text-[12px] text-[#4a5568]">New LLM answers citing <span className="font-mono-tight text-[#2c7a7b]">{client.domain}</span>, bucketed by first-seen week</div>
                  </div>
                </div>
                <div className="text-[10px] text-[#a0aec0]">Higher is better</div>
              </div>
              <div style={{ width: "100%", height: 200 }}>
                <ResponsiveContainer>
                  <BarChart
                    data={feed.weekly_history.map((b) => ({
                      ...b,
                      // Short label "3 Aug" for the X-axis
                      label: new Date(b.date + "T00:00:00Z").toLocaleDateString(undefined, { day: "numeric", month: "short" }),
                    }))}
                    margin={{ top: 5, right: 20, left: -10, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#edf2f7" vertical={false} />
                    <XAxis
                      dataKey="label"
                      stroke="#a0aec0"
                      tick={{ fontSize: 11, fill: "#718096" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                    />
                    <YAxis
                      allowDecimals={false}
                      stroke="#a0aec0"
                      tick={{ fontSize: 11, fill: "#718096" }}
                      tickLine={false}
                      axisLine={{ stroke: "#e2e8f0" }}
                    />
                    <Tooltip
                      contentStyle={{ background: "#1a202c", border: "none", borderRadius: 8, fontSize: 12, color: "white" }}
                      labelStyle={{ color: "#cbd5e0", fontSize: 11, marginBottom: 4 }}
                      formatter={(value, name) => [value, PLATFORM_META[name]?.label || name]}
                      cursor={{ fill: "#4fd1c5", fillOpacity: 0.08 }}
                    />
                    <Legend
                      iconType="square"
                      iconSize={10}
                      wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                      formatter={(name) => PLATFORM_META[name]?.label || name}
                    />
                    <Bar dataKey="google"   stackId="a" fill={PLATFORM_META.google.color}   radius={[0, 0, 0, 0]} />
                    <Bar dataKey="chat_gpt" stackId="a" fill={PLATFORM_META.chat_gpt.color} radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top cited pages strip */}
          {agg.top_pages?.length > 0 && (
            <div className="card-soft p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-2">Top cited pages</div>
              <ul className="space-y-1.5">
                {agg.top_pages.map((p, i) => (
                  <li key={p.url} className="flex items-center gap-2 text-sm">
                    <span className="font-mono-tight text-[#a0aec0] text-[10px]">#{i + 1}</span>
                    <span className="font-bold text-[#38b2ac] text-[11px] font-mono-tight tabular-nums w-6">×{p.count}</span>
                    <a href={p.url} target="_blank" rel="noreferrer" className="text-[#2c7a7b] hover:text-[#234e52] underline decoration-dotted inline-flex items-center gap-1 min-w-0 flex-1 truncate">
                      <span className="truncate">{p.title || p.url}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-1.5 flex-wrap">
              {[{ v: "all", l: "All" }, { v: "google", l: "Google AI" }, { v: "chat_gpt", l: "ChatGPT" }].map((p) => (
                <button
                  key={p.v}
                  onClick={() => setPlatformFilter(p.v)}
                  data-testid={`brand-mentions-filter-${p.v}`}
                  className={`text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg transition-colors ${
                    platformFilter === p.v ? "bg-[#1a202c] text-white" : "bg-white border border-[#e2e8f0] text-[#4a5568] hover:text-[#1a202c]"
                  }`}
                >
                  {p.l}
                </button>
              ))}
            </div>
            <div className="w-px h-4 bg-[#e2e8f0]" />
            <div className="flex items-center gap-1.5 flex-wrap">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d.value}
                  onClick={() => setDays(d.value)}
                  data-testid={`brand-mentions-days-${d.value}`}
                  className={`text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-lg transition-colors ${
                    days === d.value ? "bg-[#1a202c] text-white" : "bg-white border border-[#e2e8f0] text-[#4a5568] hover:text-[#1a202c]"
                  }`}
                >
                  {d.label}
                </button>
              ))}
            </div>
            {rows.length > 0 && (
              <button
                onClick={exportCsv}
                data-testid="brand-mentions-export-csv"
                title="Download the mentions shown as CSV"
                className="btn-ghost inline-flex items-center gap-2"
              >
                <Download className="h-3 w-3" />
                Export CSV
              </button>
            )}
            {feed?.cached && (
              <span className="text-[10px] text-[#a0aec0] ml-auto inline-flex items-center gap-1" data-testid="brand-mentions-cache-badge">
                <Info className="h-3 w-3" /> Cached · hit Refresh to re-query DataForSEO
              </span>
            )}
          </div>

          {/* Feed */}
          {rows.length === 0 ? (
            <div className="card-soft p-10 text-center text-sm text-[#a0aec0]" data-testid="brand-mentions-empty">
              No citations for <span className="font-mono-tight">{client.domain}</span> observed {days ? `in the last ${days} days` : "yet"}.
              {perPlatform.google?.total_count > 0 && days && (
                <div className="mt-2 text-[11px]">Widen the range to <button className="underline decoration-dotted" onClick={() => setDays(0)}>all time</button> to see historical mentions.</div>
              )}
            </div>
          ) : (
            <div className="space-y-2.5" data-testid="brand-mentions-feed">
              {rows.map((r, i) => {
                const key = `${r.platform}-${r.cited_url}-${i}`;
                const expanded = expandedRow === key;
                return (
                  <div key={key} className="card-soft overflow-hidden" data-testid={`brand-mentions-row-${i}`}>
                    <button
                      onClick={() => setExpandedRow(expanded ? null : key)}
                      className="w-full text-left p-5 hover:bg-[#f7fafc] transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div className="flex items-center gap-2 flex-wrap">
                          <PlatformBadge platform={r.platform} />
                          <span className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold">Question</span>
                        </div>
                        <div className="flex items-center gap-2 text-[10px] text-[#a0aec0] font-mono-tight tabular-nums">
                          {r.client_position && (
                            <span title="Your position among cited sources" className="inline-flex items-center gap-1">
                              <span className="text-[#38b2ac] font-bold">#{r.client_position}</span>
                              <span>of {r.sources_total}</span>
                            </span>
                          )}
                          {r.ai_search_volume != null && (
                            <span title="AI search volume for this prompt (DataForSEO)">
                              · {Number(r.ai_search_volume).toLocaleString()} vol
                            </span>
                          )}
                          <span title={r.last_response_at}>· {fmtObserved(r.last_response_at)}</span>
                        </div>
                      </div>
                      <div className="mt-1.5 text-sm text-[#1a202c] font-medium">{r.question || "(no question)"}</div>
                      {r.cited_url && (
                        <div className="mt-2 text-[12px] text-[#2c7a7b] inline-flex items-center gap-1 truncate max-w-full">
                          <span className="font-mono-tight text-[#a0aec0]">→</span>
                          <span className="truncate">{r.cited_title || r.cited_url}</span>
                        </div>
                      )}
                    </button>
                    {expanded && (
                      <div className="px-5 py-4 bg-[#f7fafc] border-t border-[#edf2f7]">
                        {r.answer_full && (
                          <>
                            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mb-1">Answer</div>
                            <div className="text-[13px] text-[#4a5568] whitespace-pre-wrap leading-relaxed max-h-64 overflow-y-auto">{r.answer_full}</div>
                          </>
                        )}
                        {r.sources?.length > 0 && (
                          <>
                            <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-bold mt-3 mb-1">Sources cited ({r.sources_total})</div>
                            <ul className="space-y-1">
                              {r.sources.map((s, si) => {
                                const isClient = r.client_position === si + 1;
                                return (
                                  <li key={si} className="text-[12px]">
                                    <a href={s.url} target="_blank" rel="noreferrer" className={`hover:underline decoration-dotted inline-flex items-center gap-1 break-all ${isClient ? "text-[#38b2ac] font-bold" : "text-[#2c7a7b]"}`}>
                                      <span className={`font-mono-tight ${isClient ? "text-[#38b2ac]" : "text-[#a0aec0]"}`}>#{si + 1}</span>
                                      {s.title || s.url}
                                      {isClient && <span className="text-[10px] uppercase tracking-widest bg-[#4fd1c5]/20 text-[#2c7a7b] rounded px-1">You</span>}
                                      <ExternalLink className="h-2.5 w-2.5" />
                                    </a>
                                  </li>
                                );
                              })}
                            </ul>
                          </>
                        )}
                        <div className="mt-3 text-[10px] text-[#a0aec0] flex items-center gap-2">
                          <Info className="h-3 w-3" />
                          {r.model_name && <span>Model: <span className="font-mono-tight">{r.model_name}</span></span>}
                          {r.first_response_at && r.first_response_at !== r.last_response_at && (
                            <span>· First observed: {fmtObserved(r.first_response_at)}</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Per-platform errors banner */}
          {(perPlatform.google?.error || perPlatform.chat_gpt?.error) && (
            <div className="card-soft p-3 text-[12px] text-[#c53030] flex items-start gap-2">
              <Info className="h-4 w-4 shrink-0 mt-0.5" />
              <div>
                {perPlatform.google?.error && <div>Google AI Overviews: {perPlatform.google.error}</div>}
                {perPlatform.chat_gpt?.error && <div>ChatGPT: {perPlatform.chat_gpt.error}</div>}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
